import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { simulatorJsonLd, breadcrumbJsonLd } from '../components/SEO';
import { RelatedArticlesForSimulator } from '../components/RelatedContent';
import FreelanceRevenueSimulator from '../components/simulators/FreelanceRevenueSimulator';
import SASUDirectorSalarySimulator from '../components/simulators/SASUDirectorSalarySimulator';
import CoutDirigeantSimulator from '../components/simulators/CoutDirigeantSimulator';
import MicroEntrepriseSimulator from '../components/simulators/MicroEntrepriseSimulator';
import ISvsIRSimulator from '../components/simulators/ISvsIRSimulator';
import DividendesSimulator from '../components/simulators/DividendesSimulator';
import URSSAFSimulator from '../components/simulators/URSSAFSimulator';
import TVASimulator from '../components/simulators/TVASimulator';
import ARECreationSimulator from '../components/simulators/ARECreationSimulator';
import ACRESimulator from '../components/simulators/ACRESimulator';
import AREvsARCESimulator from '../components/simulators/AREvsARCESimulator';
import DemissionChomageSimulator from '../components/simulators/DemissionChomageSimulator';
import QuelStatutSimulator from '../components/simulators/QuelStatutSimulator';
import MicroVsSASUvsEURLSimulator from '../components/simulators/MicroVsSASUvsEURLSimulator';
import QuitterMicroSimulator from '../components/simulators/QuitterMicroSimulator';
import { miniSimulators } from '../data/mock';

const SimulatorPage = () => {
  const { id } = useParams();
  
  const simulator = miniSimulators.find(sim => sim.id === id);
  
  if (!simulator) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Simulateur non trouvé</h1>
          <Link to="/simulateurs">
            <Button className="bg-blue-600 hover:bg-blue-700">
              Retour aux simulateurs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const simulatorComponents = {
    'revenu-freelance': FreelanceRevenueSimulator,
    'salaire-dirigeant-sasu': SASUDirectorSalarySimulator,
    'cout-dirigeant': CoutDirigeantSimulator,
    'micro-entreprise': MicroEntrepriseSimulator,
    'is-vs-ir': ISvsIRSimulator,
    'dividendes': DividendesSimulator,
    'urssaf': URSSAFSimulator,
    'tva': TVASimulator,
    'are-creation': ARECreationSimulator,
    'acre': ACRESimulator,
    'are-vs-arce': AREvsARCESimulator,
    'demission-chomage': DemissionChomageSimulator,
    'quel-statut': QuelStatutSimulator,
    'micro-vs-sasu-eurl': MicroVsSASUvsEURLSimulator,
    'quitter-micro': QuitterMicroSimulator,
  };

  const renderSimulator = () => {
    const Component = simulatorComponents[id];
    if (Component) return <Component />;
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <div className="text-6xl mb-4">{simulator.icon}</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{simulator.title}</h1>
        <p className="text-xl text-gray-600 mb-8">{simulator.description}</p>
        <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
          <p className="text-blue-800 font-medium">Ce simulateur sera bientôt disponible !</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <SEO
        title={simulator.title}
        description={`${simulator.description}. Simulateur gratuit avec données fiscales 2026 certifiées.`}
        path={`/simulateur/${id}`}
        jsonLd={simulatorJsonLd(simulator)}
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/simulateurs">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <span className="text-4xl">{simulator.icon}</span>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{simulator.title}</h1>
              <p className="text-gray-600 text-lg">{simulator.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Simulateur */}
      <div className="py-8">
        {renderSimulator()}
      </div>

      {/* Articles connexes */}
      <RelatedArticlesForSimulator simulatorId={id} />
    </div>
  );
};

export default SimulatorPage;